// ─────────────────────────────────────────────────────────────────────────────
// DOC-LIFECYCLE-001 (Phase D+E) — PURE Logik für Review-Drawer und Selfie-Vergleich.
// Zustandsmodell (Vertrag): missing | capturePreview | uploading | pendingReview |
// approved | rejected(reason) | renewalPending | expired — hier die Admin-Sicht:
// effective und latest GETRENNT; „pending" heißt NUR eingereicht, niemals freigegeben.
// Getestet in admin-app/scripts/tests/doc-review-logic.test.mjs (node --test).
// R1 (P1-A): Owner-/Revisions-Zuordnung vergleicht IDs als STRINGS (sameId/compareIdDesc) —
// echte UUIDv4 dürfen NIE durch Number() (→ NaN) verglichen werden.
// ─────────────────────────────────────────────────────────────────────────────
import { sameId, compareIdDesc } from './idUtil.mjs';

// Feste Reihenfolge der 6 Flächen der Selfie-Vergleichsseite (Vertrag Stufe C).
export const PANEL_ORDER = ['selfie', 'profilfoto', 'personalausweis_front', 'personalausweis_back', 'fuehrerschein_front', 'fuehrerschein_back'];

// Deutsche Labels für kanonische Slot-Keys — die Missing/Invalid-Antworten des Backends tragen nur
// {key, reason} (kein label), damit der Prüfer nicht rohe Slot-Namen wie "personalausweis_front" sieht.
const SLOT_LABELS = {
  selfie: 'Selfie',
  profilfoto: 'Profilfoto',
  personalausweis_front: 'Personalausweis – Vorderseite',
  personalausweis_back: 'Personalausweis – Rückseite',
  fuehrerschein_front: 'Führerschein – Vorderseite',
  fuehrerschein_back: 'Führerschein – Rückseite',
  p_schein_front: 'P-Schein – Vorderseite',
  p_schein_back: 'P-Schein – Rückseite',
};
export const slotLabel = (key) => SLOT_LABELS[key] || key;
const PANEL_LABELS = {
  selfie: 'Aktuelles Selfie',
  profilfoto: 'Freigegebenes Profilfoto',
  personalausweis_front: 'Ausweis / Aufenthaltstitel – Vorderseite',
  personalausweis_back: 'Ausweis / Aufenthaltstitel – Rückseite',
  fuehrerschein_front: 'Führerschein – Vorderseite',
  fuehrerschein_back: 'Führerschein – Rückseite',
};

// Bundle → IMMER 6 Flächen (Selfie + 5 Referenzen), fehlende als expliziter missing-State.
// Die Fläche verschwindet NIE — „fehlt" muss der Prüfer SEHEN.
export function bundlePanels(bundle) {
  if (!bundle) return PANEL_ORDER.map((key) => ({ key, label: PANEL_LABELS[key], missing: true }));
  const byKey = new Map((bundle.references || []).map((r) => [r.key, r]));
  return PANEL_ORDER.map((key) => {
    if (key === 'selfie') {
      const s = bundle.selfie || { missing: true };
      return { key, label: PANEL_LABELS[key], missing: !!s.missing, selfie: s.missing ? null : s };
    }
    const ref = byKey.get(key);
    if (!ref || ref.missing) return { key, label: PANEL_LABELS[key], missing: true };
    return { key, label: PANEL_LABELS[key], missing: false, reference: ref };
  });
}

// „Freigeben" bleibt DEAKTIVIERT solange: Bundle fehlt, Server can_approve=false,
// eine Pflicht-Referenz fehlt ODER die „Vergleich bestätigt"-Checkbox nicht gesetzt ist.
// (Der Server-Guard 409 missing_reference bleibt die letzte Wahrheit.)
export function canApproveBundle(bundle, vergleichBestaetigt) {
  if (!bundle) return false;
  if (!bundle.can_approve) return false;
  if ((bundle.missing_references || []).length > 0) return false;
  if (bundlePanels(bundle).some((p) => p.missing && !(p.key === 'selfie' && bundle.retained_selfie_review === true))) return false;
  return vergleichBestaetigt === true;
}

// FAIL-CLOSED-Ergänzung zu canApproveBundle: „Freigeben" bleibt zusätzlich gesperrt,
// solange nicht JEDE nicht-fehlende Fläche wirklich geladen wurde (url vorhanden, kein
// Fehler). Ein Blob-Fehler (Netzwerk/Timeout/5xx) darf NIE eine Freigabe ermöglichen,
// deren Referenz der Admin nie gesehen hat — der Server-409 greift hier nämlich nicht,
// weil das Dokument serverseitig existiert.
export function allPanesLoaded(bundle, panes) {
  if (!bundle) return false;
  return bundlePanels(bundle).every((p) => {
    if (p.missing) return true;                    // missing-State ist SICHTBAR und sperrt via canApproveBundle
    const st = panes?.[p.key];
    return !!(st && st.url && !st.error && !st.loading);
  });
}

// Lade-Guard je Fläche: neu laden, wenn noch keine Fläche existiert ODER die geladene
// Fläche zu einem ANDEREN Dokument gehört (forId). Ersetzt die früheren '!panes.X'-Guards,
// die nach einem Doc-/Bundle-Wechsel nie neu luden (tote, revozierte blob:-URLs).
export function paneNeedsLoad(pane, forId) {
  if (!pane) return true;
  return pane.forId !== forId;
}

// Ablehnungsgrund ist PFLICHT (Backend: 400 reason_required) — kein Leerraum-Trick.
export function canSubmitRejection(reason) {
  return typeof reason === 'string' && reason.trim().length > 0;
}

// Vorder-/Rückseiten-Paarung (…_front ↔ …_back); einseitige Typen haben kein Gegenüber.
export function counterpartType(documentType) {
  if (typeof documentType !== 'string') return null;
  if (documentType.endsWith('_front')) return `${documentType.slice(0, -6)}_back`;
  if (documentType.endsWith('_back')) return `${documentType.slice(0, -5)}_front`;
  return null;
}

// Aktuell WIRKSAME Revision desselben Owner+Typs (is_effective) — nie das Dokument selbst,
// nie pending: „aktuelle freigegebene Version NEBEN neuer Revision".
export function effectiveSibling(docs, doc) {
  if (!doc) return null;
  return (docs || []).find((d) =>
    !sameId(d.id, doc.id) && d.owner_type === doc.owner_type && sameId(d.owner_id, doc.owner_id)
    && d.document_type === doc.document_type && Number(d.is_effective) === 1
  ) || null;
}

// Zustand der dritten Drawer-Fläche „Aktuell freigegeben" — DREI Fälle sauber getrennt:
//   'self'    → die betrachtete Revision IST die wirksame (is_effective=1): kein roter
//               Missing-State („Es gibt noch keine wirksame Version" wäre faktisch falsch),
//               sondern Hinweis „Diese Ansicht IST die aktuell wirksame Version".
//   'sibling' → es gibt eine andere wirksame Revision → Vergleichsstand daneben.
//   'none'    → echter Erstupload ohne wirksame Version → expliziter Missing-State.
export function effectivePane(docs, doc) {
  if (!doc) return { kind: 'none', sibling: null };
  if (Number(doc.is_effective) === 1) return { kind: 'self', sibling: null };
  const sibling = effectiveSibling(docs, doc);
  return sibling ? { kind: 'sibling', sibling } : { kind: 'none', sibling: null };
}

// Queue-Ansicht: ein LADEFEHLER darf NIE wie eine leere (= fertig abgearbeitete) Prüf-Queue
// aussehen — Fehler hat Vorrang vor „Keine Dokumente gefunden".
export function queueViewState(docs, loadError) {
  if (loadError) return 'error';
  if (!docs || docs.length === 0) return 'empty';
  return 'list';
}

// Revisions-Historie desselben Owner+Typs, deterministisch wie das Backend:
// (COALESCE(revision,1) DESC, id DESC) — nie created_at.
export function revisionHistory(docs, doc) {
  if (!doc) return [];
  return (docs || [])
    .filter((d) => d.owner_type === doc.owner_type && sameId(d.owner_id, doc.owner_id) && d.document_type === doc.document_type)
    .sort((a, b) => ((Number(b.revision) || 1) - (Number(a.revision) || 1)) || compareIdDesc(a.id, b.id));
}

// Deutsche Status-Labels (lifecycle_status + abgeleitete Zustände).
const LIFECYCLE_LABELS = {
  pending_review: 'Eingereicht – in Prüfung',
  approved: 'Freigegeben',
  rejected: 'Abgelehnt',
  superseded: 'Ersetzt',
  expired: 'Abgelaufen',
  missing: 'Fehlt',
  compliance_locked: 'Compliance-gesperrt',
  // Katalog-Kurzformen (document_status-Punkte):
  pending: 'Eingereicht – in Prüfung',
};
export function lifecycleLabel(status) { return LIFECYCLE_LABELS[status] || status || '–'; }

// Farben je lifecycle_status (Grau/Schwarz/Weiß + Rot Fehler + Violett CI, sparsam).
export function lifecycleColor(status) {
  switch (status) {
    case 'approved': return { color: '#9C27B0', bg: '#f3e5f5' };
    case 'rejected': return { color: '#ef4444', bg: '#fef2f2' };
    case 'expired': return { color: '#b91c1c', bg: '#fef2f2' };
    case 'superseded': return { color: '#6b7280', bg: '#f9fafb' };
    case 'pending':
    case 'pending_review': return { color: '#6b7280', bg: '#f3f4f6' };
    default: return { color: '#6b7280', bg: '#f3f4f6' };
  }
}

// 409 documents_incomplete verständlich machen: je Blocker Label + deutscher Status.
const BLOCKER_STATUS = {
  missing: 'fehlt noch',
  pending: 'noch in Prüfung (nicht freigegeben)',
  rejected: 'abgelehnt – neuer Upload nötig',
  expired: 'abgelaufen – Erneuerung nötig',
  compliance_locked: 'Compliance-gesperrt',
};
export function formatBlockers(blockers) {
  const list = (blockers || []).map((b) => `• ${b.label || b.key}: ${BLOCKER_STATUS[b.status] || b.status}`);
  if (!list.length) return 'Pflichtdokumente unvollständig.';
  return `Freigabe nicht möglich – Pflichtdokumente unvollständig:\n${list.join('\n')}`;
}

// R1 (Selfie-Freigabe): 409 reference_invalid — eine snapshotgebundene Referenz wurde nach dem
// Laden entwertet. Server-Gründe (verification.validateReferences) deutsch benennen.
const INVALID_REASON_LABELS = {
  foreign: 'gehört einem anderen Fahrer',
  type_mismatch: 'falscher Dokumenttyp für diese Fläche',
  rejected: 'abgelehnt – neuer Upload nötig',
  expired: 'abgelaufen – Erneuerung nötig',
  file_missing: 'Datei nicht abrufbar',
  gone: 'nicht mehr vorhanden',
  not_approved: 'nicht (mehr) freigegeben',
};
export function invalidReasonLabel(reason) { return INVALID_REASON_LABELS[reason] || 'ungültig'; }

// 409 reference_invalid verständlich: je ungültiger Referenz Fläche + deutscher Grund.
export function formatInvalidReferences(invalid) {
  const list = (invalid || []).map((it) => `• ${it.label || it.key}: ${invalidReasonLabel(it.reason)}`);
  if (!list.length) return 'Freigabe nicht möglich – eine Referenz ist ungültig.';
  return `Freigabe nicht möglich – ungültige Referenz(en):\n${list.join('\n')}`;
}

// „Freigeben" bleibt gesperrt, solange der Server eine Referenz als FEHLEND (missing_reference)
// ODER als UNGÜLTIG (reference_invalid) meldet — beide sperren fail-closed.
export function hasBlockingReferenceIssue(missing, invalid) {
  return (missing || []).length > 0 || (invalid || []).length > 0;
}

// Einheitliche deutsche Fehlermeldung für eine Freigabe-/Ablehn-Entscheidung über die dedizierten
// Lifecycle-Routen (…/approve|…/reject). Deckt den generic-PATCH-403 (use_lifecycle_endpoint),
// das Dokument-Gate (409 documents_incomplete), den Pflichtgrund (400 reason_required) und die
// zweite Entscheidung (409 already_decided) ab; sonst die Server-Meldung bzw. ein Fallback.
export function decisionErrorMessage(data) {
  if (!data) return 'Fehler bei der Entscheidung.';
  switch (data.code) {
    case 'use_lifecycle_endpoint':
      return 'Freigabe/Ablehnung ist nur über die Freigaben-Funktion möglich (Berechtigung „freigaben.manage"). Bitte die Seite neu laden und erneut versuchen.';
    case 'documents_incomplete':
      return formatBlockers(data.blockers);
    case 'reason_required':
      return 'Ablehnungsgrund ist Pflicht.';
    case 'already_decided':
      return 'Bereits entschieden — Ansicht wird aktualisiert.';
    default:
      return data.error || 'Fehler bei der Entscheidung.';
  }
}
